import type { ApplicationContext } from '@hc-frontend/core-context';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import { UpdateProspectLandingUrlParameters } from '../../entities';
import type {
  UpdateProspectLandingUrlRepository,
  UpdateProspectLandingUrlResult,
} from '../../repositories';
import type { GetProspectUseCase } from '../get-prospect/get-prospect';
import { hasAcquisitionParams, hasLandingChanged } from './has-landing-changed';

export type UpdateProspectLandingUrlUseCase = (
  parameters: UpdateProspectLandingUrlParameters,
) => Promise<UpdateProspectLandingUrlResult>;

export type UpdateProspectLandingUrlFactoryParams = Pick<
  ApplicationContext,
  'log'
> & {
  updateProspectLandingUrl: UpdateProspectLandingUrlRepository;
  getProspect: GetProspectUseCase;
};

/**
 * Refreshes the `landingUrl` stored on an existing prospect when a returning
 * visitor re-enters on a **new** marketing landing while keeping the same
 * session (operation `GET`, so no new session/lead is created).
 *
 * The update is skipped — as a no-op success — when:
 * - the current request carries no acquisition params (in-funnel navigation);
 * - its acquisition matches what is already stored; or
 * - the prospect has already been **sold**. A lead that was priced, routed and
 *   delivered under one acquisition must keep it, otherwise the stored campaign
 *   no longer matches the one the buyer purchased against. In production ~3.8%
 *   of leads reach a sold status and 89% of those do so within 5 minutes, so
 *   this only bites on returns that arrive after the lead was already sold.
 */
export function updateProspectLandingUrlFactory({
  updateProspectLandingUrl,
  getProspect,
  log,
}: UpdateProspectLandingUrlFactoryParams): UpdateProspectLandingUrlUseCase {
  return async (parameters) => {
    const parsedParams =
      UpdateProspectLandingUrlParameters.safeParse(parameters);

    if (!parsedParams.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToUpdateProspectLandingUrl',
        data: {
          rootCause: parsedParams.error,
        },
      });
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToUpdateProspectLandingUrl',
            rootCause: parsedParams.error,
          },
        ],
      });
    }

    // No acquisition signal on this request → not a fresh landing. Skip before
    // the prospect lookup so ordinary in-funnel navigation costs nothing.
    if (!hasAcquisitionParams(parsedParams.data.landingUrl)) {
      return createSuccessOperationResult(undefined);
    }

    const currentProspect = await getProspect(
      parsedParams.data.sessionId,
      parsedParams.data.userId,
    );

    // Already sold → the stored acquisition is what the lead was priced and
    // bought against. Leave it alone, but log so the rate is measurable.
    if (currentProspect.success && currentProspect.data.sold) {
      log({
        context: 'server',
        type: 'info',
        message: 'skippedProspectLandingUrlRefreshOnSoldLead',
        data: {
          sessionId: parsedParams.data.sessionId,
        },
      });
      return createSuccessOperationResult(undefined);
    }

    const storedLandingUrl = currentProspect.success
      ? (currentProspect.data.landingUrl ?? '')
      : '';

    if (!hasLandingChanged(storedLandingUrl, parsedParams.data.landingUrl)) {
      return createSuccessOperationResult(undefined);
    }

    const result = await updateProspectLandingUrl(parsedParams.data);

    if (!result.success) {
      log({
        context: 'server',
        type: 'error',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });
    } else {
      log({
        context: 'server',
        type: 'info',
        message: 'updatedProspectLandingUrlOnReturn',
        data: {
          sessionId: parsedParams.data.sessionId,
        },
      });
    }

    return result;
  };
}
